import { z } from 'zod'
import { FUNNEL_STAGES, FUNNEL_TARGETS } from '../enums/funnel-stage.enum.js'
import { leadStatsViewSchema } from '../enums/lead-stats-view.enum.js'
import { PIPELINE_YEAR_DAYS, pipelineComparisonSchema, pipelineSpanSchema } from '../enums/pipeline-span.enum.js'
import { lostReasonCountKeySchema } from '../enums/lost-reason.enum.js'
import { calendarDaySchema, instantSchema } from '../validators/temporal.validator.js'
import { PIPELINE_VERDICTS } from '../constants/pipeline.js'
import { adPeriodWindowDtoSchema } from './ad-numbers.dto.js'
import { leadStatsTrendPointDtoSchema } from './lead-stats-numbers.dto.js'
import { sourceRowDtoSchema, sourceSummaryDtoSchema } from './source-numbers.dto.js'

/**
 * A venue's or the company's pipeline for one period (docs/pipeline-view.spec.md):
 * every funnel stage from Inquiry to Event day, then Lost and Canceled, with
 * each count's change, trend, cost and conversions, worked out by the API from
 * what is stored.
 */

/** One of the couples a pipeline can show: `all`, `unlinked`, or a lead stats pipeline's id. */
export const pipelineSplitOptionDtoSchema = z.object({
  value: z.string(),
  label: z.string(),
})
export type PipelineSplitOptionDto = z.infer<typeof pipelineSplitOptionDtoSchema>

/**
 * How a step's couples compare with what's usual at the venue
 * (docs/pipeline-view.spec.md, "What's usual"), in *this period's leads* only.
 * The couples judged are those at the step before; where the venue's timing
 * isn't known yet, only those who inquired at least eight weeks ago.
 */
export const pipelineStageUsualDtoSchema = z.object({
  /** The couples judged, and how many of them reached the step. */
  part: z.number().int().nonnegative(),
  whole: z.number().int().positive(),
  /** The share of the year before's couples who went on to it, once they'd all decided. */
  share: z.number().min(0).max(1),
  /** The share the year before's couples had reached at these couples' ages: what's usual by now. */
  byNow: z.number().min(0).max(1),
  /** How many couples short of the usual by now (negative: beyond it). Fractional: an expectation. */
  gap: z.number(),
  verdict: z.enum(PIPELINE_VERDICTS),
  /** Couples at the step before who haven't reached it yet and are younger than most couples take to. */
  stillDeciding: z.number().int().nonnegative(),
  /**
   * Only couples old enough to judge are counted in `part` and `whole` (eight
   * weeks, while the venue's timing isn't known), not every couple at the step
   * before: the page says so beside the share.
   */
  settledOnly: z.boolean().default(false),
})
export type PipelineStageUsualDto = z.infer<typeof pipelineStageUsualDtoSchema>

/** One stage or outcome for the period chosen. A count is null when nothing at the venue records it: "Not tracked". */
export const pipelineStageDtoSchema = z.object({
  target: z.enum(FUNNEL_TARGETS),
  tracked: z.boolean(),
  value: z.number().int().nonnegative().nullable(),
  /** The period before, as it stands now or, when `sameAge`, as it stood at the same age. */
  previous: z.number().int().nonnegative().nullable(),
  sameAge: z.boolean(),
  /** The period's couples are still moving through it. */
  stillMoving: z.boolean(),
  /** Each two-week period of the last 12 months, oldest first, ending with the span's newest. */
  trend: z.array(leadStatsTrendPointDtoSchema),
  /**
   * What marketing cost over the span (`cost`) divided by the count, in micros
   * of its currency: for Inquiry, Toured and Booked only, from three couples up;
   * null otherwise and when costs aren't shown.
   */
  costPerMicros: z.number().int().nonnegative().nullable(),
  /** Of the period's inquiries, how many reached it (0.25 is 25%). Only in "this period's leads". */
  shareOfInquiries: z.number().min(0).max(1).nullable(),
  /** Of the couples at the stage before, how many reached it. Only in "this period's leads". */
  shareOfPrevious: z.number().min(0).max(1).nullable(),
  /** What the stage before is: the closest tracked one above, both routes to a tour for Toured, Booked for Canceled. */
  before: z.array(z.enum(FUNNEL_STAGES)),
  /** How it compares with what's usual; null where there isn't enough history to say, and in *what happened*. */
  usual: pipelineStageUsualDtoSchema.nullable().default(null),
})
export type PipelineStageDto = z.infer<typeof pipelineStageDtoSchema>

/** Lost, by why: the fixed reasons in order, then "Not recorded". */
export const pipelineLostReasonDtoSchema = z.object({
  reason: lostReasonCountKeySchema,
  count: z.number().int().nonnegative(),
  /**
   * Of `count`, the losses after the couple toured: price and other venues, a
   * question of the sale; the rest, before a tour, are mostly silence, a
   * question of follow-up. Null where nothing records a tour.
   */
  afterTour: z.number().int().nonnegative().nullable().default(null),
})
export type PipelineLostReasonDto = z.infer<typeof pipelineLostReasonDtoSchema>

const spendSchema = z.object({ spendMicros: z.number().int().nonnegative(), currencyCode: z.string() })

/**
 * The days a pipeline shows (docs/pipeline-view.spec.md, "Spans"): from the
 * first day of a span's oldest period to the last of its newest, and the days
 * they're compared with. A period window's shape, holding up to a year of days.
 */
export const pipelineWindowDtoSchema = adPeriodWindowDtoSchema.extend({ days: z.number().int().min(1).max(PIPELINE_YEAR_DAYS) })
export type PipelineWindowDto = z.infer<typeof pipelineWindowDtoSchema>

/**
 * What marketing cost over the span, Google Ads and listing fees together: what
 * each priced count's cost is worked out from. `listingsMicros` is the listing
 * fees' part, in US dollars' micros; null without any.
 */
const costSchema = z.object({
  micros: z.number().int().nonnegative(),
  currencyCode: z.string(),
  listingsMicros: z.number().int().nonnegative().nullable(),
})

/**
 * How the span's bookings are pacing against the usual at the same age
 * (docs/pipeline-view.spec.md, "Still deciding"), period by period, oldest
 * first. Null in *what happened*, and until the venue's booking timing is known.
 */
const paceSchema = z.object({
  /** Most couples who book here do so within this many days of inquiring. */
  bookWithinDays: z.number().int().nonnegative(),
  periods: z.array(
    z.object({
      start: calendarDaySchema,
      inquiries: z.number().int().nonnegative(),
      booked: z.number().int().nonnegative(),
      /** How many of its couples would usually have booked by now. Fractional: an expectation. */
      usualByNow: z.number().nonnegative(),
    }),
  ),
  /** How many more bookings these couples usually go on to make. Fractional. */
  stillToCome: z.number().nonnegative(),
})

/**
 * How many couples are in each of the lists a Pipeline page points at
 * (`PIPELINE_COUPLE_LISTS`); null for a list the venue doesn't record what it
 * needs for (tours, proposals).
 */
const lookAtSchema = z.object({
  touredDeciding: z.number().int().nonnegative().nullable(),
  touredLost: z.number().int().nonnegative().nullable(),
  touredWaiting: z.number().int().nonnegative().nullable(),
  /** Of those waiting, the ones who haven't been sent a proposal. */
  touredWaitingNoProposal: z.number().int().nonnegative().nullable(),
  holdsEnding: z.number().int().nonnegative(),
  quietBeforeTour: z.number().int().nonnegative().nullable(),
})

/** What the numbers can't see (docs/pipeline-view.spec.md, "Tracking"), each with what would fix it. */
const trackingSchema = z.object({
  /** Where inquiries come in from besides the venue's own events. */
  connections: z.array(z.enum(['gohighlevel', 'honeybook'])),
  /** Lead source names the venue hasn't said the meaning of, with how many of the span's inquiries carry each, most first: up to five. */
  unmappedSources: z.array(z.object({ name: z.string(), inquiries: z.number().int().positive() })),
  /** How many of the span's inquiries carry any of them, the ones not listed too. */
  unmappedInquiries: z.number().int().nonnegative().default(0),
  /** The span's booked couples with no lead, where inquiries come in from a connection: where they came from is unknown. */
  bookingsWithoutLead: z.number().int().nonnegative(),
})

/** What a typical booking is worth: the average over the span's and the year before's, for whoever sees ad costs. */
const averageBookingSchema = z.number().int().nonnegative().nullable().default(null)

/** The span shown: how many weeks, and what they're compared with. */
const spanFields = {
  span: pipelineSpanSchema.default('two_weeks'),
  compare: pipelineComparisonSchema.default('before'),
  pace: paceSchema.nullable().default(null),
  averageBookingCents: averageBookingSchema,
}

const periodsSchema = z.array(z.object({ start: calendarDaySchema, inProgress: z.boolean() }))

export const venuePipelineDtoSchema = z.object({
  venueId: z.string().uuid(),
  venueName: z.string(),
  timeZone: z.string(),
  /** All couples, each of the company's lead stats pipelines, then Not linked to a lead. */
  splits: z.array(pipelineSplitOptionDtoSchema),
  split: z.string(),
  view: leadStatsViewSchema,
  ...spanFields,
  /** Every period there are numbers for, newest first. */
  periods: periodsSchema,
  /**
   * The days shown, from the first day of the span's oldest period to its
   * newest's last, and the days they're compared with. Null when there are none.
   */
  window: pipelineWindowDtoSchema.nullable(),
  /** Every stage, then Lost and Canceled. */
  stages: z.array(pipelineStageDtoSchema),
  lostReasons: z.array(pipelineLostReasonDtoSchema),
  /** The venue's ad spend over the span: on All couples, to whoever sees ad costs, when there was any. */
  spend: spendSchema.nullable(),
  /** What marketing cost over the span; null where `spend` is, or when its parts can't be added up. */
  cost: costSchema.nullable().default(null),
  /**
   * Where the period's couples came from, one row per source
   * (docs/ad-attribution.spec.md): on All couples only, its money for whoever
   * sees ad costs.
   */
  sources: z.array(sourceRowDtoSchema).default([]),
  /** The period's booked value against everything it cost: on All couples, for whoever sees ad costs. */
  sourceSummary: sourceSummaryDtoSchema.nullable().default(null),
  /** The same for the days the span is compared with, as they stand now. */
  compared: sourceSummaryDtoSchema.nullable().default(null),
  /** The oldest complete GoHighLevel read among working connections; null without one. */
  updatedAt: instantSchema.nullable(),
  /** The last complete HoneyBook read; null without a HoneyBook connection, or before its first. */
  honeybookUpdatedAt: instantSchema.nullable().default(null),
  /**
   * The venue day HoneyBook's recording began, when the period shown or the one
   * it is compared with starts before it: those days are partly recorded, since
   * HoneyBook dated each inquiry and each lead's current stage but not the steps
   * before. Null otherwise.
   */
  partlyRecordedBefore: calendarDaySchema.nullable().default(null),
  /** The venue day it switched from HoneyBook to FLOH Venues: earlier periods are counted from HoneyBook. Null otherwise. */
  switchedOn: calendarDaySchema.nullable().default(null),
  /** Whether this person may open the couples behind a count. */
  canListCouples: z.boolean(),
  /** How many couples are in each list worth a look; null without a window. */
  lookAt: lookAtSchema.nullable().default(null),
  /** What the numbers can't see; null without a window. */
  tracking: trackingSchema.nullable().default(null),
})
export type VenuePipelineDto = z.infer<typeof venuePipelineDtoSchema>

/**
 * The pipeline email's switch at a company without GoHighLevel (docs/pipeline-view.spec.md,
 * "The email"). A company with GoHighLevel sends it in its lead stats email, under that switch.
 */
export const pipelineEmailDtoSchema = z.object({
  on: z.boolean(),
  /** Organization admins turn it on and off. */
  canChange: z.boolean(),
})
export type PipelineEmailDto = z.infer<typeof pipelineEmailDtoSchema>

/** The company's pipeline: its whole funnel, then a row per venue. */
export const companyPipelineDtoSchema = z.object({
  splits: z.array(pipelineSplitOptionDtoSchema),
  split: z.string(),
  view: leadStatsViewSchema,
  ...spanFields,
  periods: periodsSchema,
  window: pipelineWindowDtoSchema.nullable(),
  /** Couples added up across venues; conversions and costs worked out from those totals. */
  stages: z.array(pipelineStageDtoSchema),
  lostReasons: z.array(pipelineLostReasonDtoSchema),
  spend: spendSchema.nullable(),
  cost: costSchema.nullable().default(null),
  /** The venues' booked value against everything they cost, added up: on All couples, for whoever sees ad costs. */
  sourceSummary: sourceSummaryDtoSchema.nullable().default(null),
  compared: sourceSummaryDtoSchema.nullable().default(null),
  /** Every live venue, by name. A value is null where the venue doesn't track the stage. */
  rows: z.array(
    z.object({
      venueId: z.string().uuid(),
      venueName: z.string(),
      stillMoving: z.boolean(),
      /** Each stage as the venue's own page counts it, for the venues table: its change, shares, cost and what's usual. */
      values: z.array(
        z.object({
          target: z.enum(FUNNEL_TARGETS),
          value: z.number().int().nonnegative().nullable(),
          previous: z.number().int().nonnegative().nullable().default(null),
          sameAge: z.boolean().default(false),
          shareOfInquiries: z.number().min(0).max(1).nullable().default(null),
          shareOfPrevious: z.number().min(0).max(1).nullable().default(null),
          before: z.array(z.enum(FUNNEL_STAGES)).default([]),
          costPerMicros: z.number().int().nonnegative().nullable().default(null),
          usual: pipelineStageUsualDtoSchema.nullable().default(null),
        }),
      ),
      /** What the venue's marketing cost, booked and returned, and the same for the days compared with, for whoever sees ad costs. */
      cost: costSchema.nullable().default(null),
      sourceSummary: sourceSummaryDtoSchema.nullable().default(null),
      compared: sourceSummaryDtoSchema.nullable().default(null),
      /** What the venue's numbers can't see. */
      tracking: trackingSchema.nullable().default(null),
    }),
  ),
  /** The email's switch; null at a company with GoHighLevel, whose switch is on its GoHighLevel page. */
  email: pipelineEmailDtoSchema.nullable(),
})
export type CompanyPipelineDto = z.infer<typeof companyPipelineDtoSchema>

/** One couple behind a count. */
export const pipelineCoupleDtoSchema = z.object({
  /** Its event's name, else its GoHighLevel contact's, looked up now and never kept; null when that couldn't be done. */
  name: z.string().nullable(),
  /** When they reached the stage; null when it was before records began. */
  reachedAt: instantSchema.nullable(),
  /** Where they are now: their furthest stage, or Lost or Canceled. */
  now: z.enum(FUNNEL_TARGETS),
  event: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** Each GoHighLevel sub-account they are in, with a link to the contact there. */
  leads: z.array(z.object({ locationName: z.string(), url: z.string() })),
  /** Where they came from, as the page's sources name it. */
  source: z.string().nullable().default(null),
  /** When they toured; null when they haven't, or nothing records it. */
  touredAt: instantSchema.nullable().default(null),
  /** Whether they've been sent a proposal; null where nothing records proposals. */
  proposalSent: z.boolean().nullable().default(null),
  /** The last day of their hold, while one isn't released. */
  holdUntil: calendarDaySchema.nullable().default(null),
  /** Why they were lost, while they are. */
  lostReason: lostReasonCountKeySchema.nullable().default(null),
})
export type PipelineCoupleDto = z.infer<typeof pipelineCoupleDtoSchema>

/**
 * A page of the couples behind a count, newest first and those from before
 * records began last; or of a list, in the order it reads (longest waiting
 * first, holds soonest first, the newest loss first).
 */
export const pipelineCouplesDtoSchema = z.object({
  couples: z.array(pipelineCoupleDtoSchema),
  /** Every couple behind the count, across pages. */
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
})
export type PipelineCouplesDto = z.infer<typeof pipelineCouplesDtoSchema>
